import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  callbackToQb,
  connectQb,
  getQbProducts,
  getQbRealmId,
  getQbTax,
  sendToQb,
  bulkSendToQb,
  getQbItems,
  getQbServiceCategoryMappings,
  mapQbServiceCategories,
  deleteQbServiceCategoryMapping,
} from "controller/api";
import { ApiErrorType } from "shared";
import type {
  QbServiceCategory,
  QbServiceCategoryMappingInput,
} from "shared/types/integrations";
import { sentryLogger } from "utilz";

export const useConnectQb = () => {
  return useMutation({
    mutationFn: () => connectQb(),
    onSuccess(data, variables, context) {},
    onError: (error: ApiErrorType, variables, context) => {
      //   sentryLogger({
      //     error: error,
      //     data: variables,
      //     workflow: "integration",
      //     level: "error",
      //     functionName: "userGetNinjaOAuthToken",
      //   });
    },
  });
};
export const useCallbackToQb = () => {
  return useMutation({
    mutationFn: ({
      state,
      code,
      realmId,
      secret,
      originalUrl,
    }: {
      state: string;
      code: string;
      realmId: string;
      secret: string;
      originalUrl: string;
    }) =>
      callbackToQb({
        state,
        code,
        realmId,
        secret,
        originalUrl,
      }),
    onSuccess(data, variables, context) {},
    onError: (error: ApiErrorType, variables, context) => {
      //   sentryLogger({
      //     error: error,
      //     data: variables,
      //     workflow: "integration",
      //     level: "error",
      //     functionName: "userGetNinjaOAuthToken",
      //   });
    },
  });
};

export const useGetQbProducts = () => {
  return useQuery({
    queryKey: ["qbProducts"],
    queryFn: () => getQbProducts(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "sendToQb",
      });
    },
  });
};

export const useGetQbTax = () => {
  return useQuery({
    queryKey: ["qbTax"],
    queryFn: () => getQbTax(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        // data: variables,
        workflow: "integration",
        level: "error",
        functionName: "sendToQb",
      });
    },
  });
};

export const useSendToQb = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      currency,
      qbRealmId,
    }: {
      invoiceId: string;
      currency: string;
      qbRealmId: string;
    }) => sendToQb({ invoiceId, currency, qbRealmId }),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["invoices"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      queryClient.invalidateQueries(["invoices"]);
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "sendToQb",
      });
    },
  });
};

export const useBulkSendToQb = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceIds,
      currency,
      qbRealmId,
    }: {
      invoiceIds: string[];
      currency: string;
      qbRealmId: string;
    }) => bulkSendToQb({ invoiceIds, currency, qbRealmId }),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["invoices"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      queryClient.invalidateQueries(["invoices"]);
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "bulkSendToQb",
      });
    },
  });
};

export const useGetRealmId = () => {
  return useQuery({
    queryKey: ["realmId"],
    queryFn: () => getQbRealmId(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getQbRealmId",
      });
    },
  });
};

export const useGetQbItems = () => {
  return useQuery({
    queryKey: ["qbItems"],
    queryFn: () => getQbItems(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getQbItems",
      });
    },
  });
};

export const useGetQbServiceCategoryMappings = () => {
  return useQuery({
    queryKey: ["qbServiceCategoryMappings"],
    queryFn: () => getQbServiceCategoryMappings(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getQbServiceCategoryMappings",
      });
    },
  });
};

export const useMapQbServiceCategories = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mappings: QbServiceCategoryMappingInput[]) =>
      mapQbServiceCategories(mappings),
    onSuccess() {
      queryClient.invalidateQueries(["qbServiceCategoryMappings"]);
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "mapQbServiceCategories",
      });
    },
  });
};

export const useDeleteQbServiceCategoryMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (category: QbServiceCategory) =>
      deleteQbServiceCategoryMapping(category),
    onSuccess() {
      queryClient.invalidateQueries(["qbServiceCategoryMappings"]);
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "deleteQbServiceCategoryMapping",
      });
    },
  });
};
